import { useEffect, useRef, useState } from "react";
import { RefreshCw, WifiOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, CONNECTION_EVENT } from "@/lib/api";

const IDLE_MS = 60_000;
const HEARTBEAT_MS = 120_000;
const MESSAGES = {
  offline: "Sem conexão com o servidor. Verifique sua internet e atualize a página.",
  expired: "Sua sessão expirou por inatividade. Atualize a página para entrar de novo.",
  back: "A conexão voltou. Atualize a página para ver as informações mais recentes.",
};

export function ConnectionBanner() {
  const { user } = useAuth();
  const [issue, setIssue] = useState(null);
  const active = ["reseller", "store"].includes(user?.role);
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    setIssue(null);
    if (!active) return undefined;
    let hiddenAt = null;
    const recovered = () => setIssue((cur) => (cur === "offline" ? "back" : cur));
    const check = () => api.get("/auth/me").then(recovered).catch(() => {});
    const onSignal = (e) => { if (activeRef.current) setIssue((cur) => (cur === "expired" ? cur : e.detail)); };
    const onOffline = () => setIssue("offline");
    const onVisibility = () => {
      if (document.hidden) { hiddenAt = Date.now(); return; }
      if (hiddenAt && Date.now() - hiddenAt > IDLE_MS) check();
    };
    const timer = setInterval(() => { if (!document.hidden) check(); }, HEARTBEAT_MS);
    window.addEventListener(CONNECTION_EVENT, onSignal);
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", check);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(timer);
      window.removeEventListener(CONNECTION_EVENT, onSignal);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", check);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [active, user?.user_id]);

  if (!active || !issue) return null;
  const ok = issue === "back";
  return (
    <div role="alert" data-testid="connection-banner" data-issue={issue}
      className={`sticky top-0 z-50 flex flex-wrap items-center justify-center gap-3 px-4 py-3 text-sm font-semibold ${ok ? "bg-emerald-600 text-white" : "bg-amber-400 text-slate-900"}`}>
      {!ok && <WifiOff className="h-4 w-4 shrink-0" />}
      <span data-testid="connection-banner-message">{MESSAGES[issue]}</span>
      <button type="button" onClick={() => window.location.reload()} data-testid="btn-connection-reload"
        className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-1.5 text-xs font-bold text-white hover:bg-slate-700">
        <RefreshCw className="h-3.5 w-3.5" /> Atualizar página
      </button>
    </div>
  );
}
